import { useEffect, useState } from 'react';
import { AlertTriangle, Building2, KeyRound, Loader2, LogOut, Mail, Save, Shield, Trash2, User } from 'lucide-react';
import { PageShell } from '@/components/layout/PageShell';
import { useAuth, signOut } from '@/hooks/useAuth';
import { useTenant } from '@/hooks/useTenant';
import { supabase } from '@/lib/supabase';
import { useNavigate } from 'react-router-dom';

export function Settings() {
  const { user } = useAuth();
  const { tenantId } = useTenant();
  const navigate = useNavigate();

  const [companyName, setCompanyName] = useState<string>('');
  const [companyDoc, setCompanyDoc]   = useState<string>('');
  const [loadingCompany, setLoadingCompany] = useState(true);
  const [savingCompany, setSavingCompany]   = useState(false);
  const [companyMsg, setCompanyMsg]   = useState<{ ok: boolean; text: string } | null>(null);

  // Exclusão de conta
  const [deleteOpen, setDeleteOpen]   = useState(false);
  const [deleteText, setDeleteText]   = useState('');
  const [deleting, setDeleting]       = useState(false);
  const [deleteErr, setDeleteErr]     = useState('');
  const [surveyStep, setSurveyStep]   = useState(false);
  const [surveyReason, setSurveyReason]   = useState('');
  const [surveyComment, setSurveyComment] = useState('');

  const [sendingRecovery, setSendingRecovery] = useState(false);
  const [recoveryMsg, setRecoveryMsg] = useState<string | null>(null);
  const [recoveryErr, setRecoveryErr] = useState<string | null>(null);
  const [recoveryCooldown, setRecoveryCooldown] = useState(0);

  useEffect(() => {
    if (!tenantId) { setLoadingCompany(false); return; }
    supabase
      .from('tenants')
      .select('name, document')
      .eq('id', tenantId)
      .single()
      .then(({ data }) => {
        const t = data as { name: string; document: string | null } | null;
        setCompanyName(t?.name ?? '');
        setCompanyDoc(t?.document ?? '');
        setLoadingCompany(false);
      });
  }, [tenantId]);

  async function handleSaveCompany() {
    if (!tenantId || !companyName.trim()) return;
    setSavingCompany(true);
    setCompanyMsg(null);
    const { error } = await supabase
      .from('tenants')
      .update({ name: companyName.trim(), document: companyDoc.trim() || null } as never)
      .eq('id', tenantId);
    setCompanyMsg(error
      ? { ok: false, text: 'Erro ao salvar. Apenas owners/admins podem editar a empresa.' }
      : { ok: true, text: 'Dados da empresa atualizados.' });
    setSavingCompany(false);
  }

  async function handleDeleteAccount() {
    setDeleting(true);
    setDeleteErr('');
    try {
      const { data, error } = await supabase.functions.invoke('delete-account', {
        body: {
          confirm: 'EXCLUIR',
          reason: surveyReason || null,
          survey: { reason: surveyReason || null, comment: surveyComment.trim() || null },
        },
      });
      if (error) throw new Error(error.message);
      if (!data?.ok) throw new Error(data?.error ?? 'Falha ao excluir a conta.');
      await signOut();
      navigate('/login');
    } catch (e: any) {
      setDeleteErr(e.message ?? 'Erro ao excluir a conta.');
      setDeleting(false);
    }
  }

  useEffect(() => {
    if (recoveryCooldown <= 0) return;
    const t = setTimeout(() => setRecoveryCooldown((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [recoveryCooldown]);

  async function handleSignOut() {
    await signOut();
    navigate('/login');
  }

  async function handleChangePassword() {
    if (!user?.email || recoveryCooldown > 0) return;
    setSendingRecovery(true);
    setRecoveryMsg(null);
    setRecoveryErr(null);
    const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
      redirectTo: `${window.location.origin}/login`,
    });
    if (error) {
      setRecoveryErr('Não foi possível enviar o e-mail. Tente novamente.');
    } else {
      setRecoveryMsg(`E-mail de redefinição enviado para ${user.email}.`);
      setRecoveryCooldown(60);
    }
    setSendingRecovery(false);
  }

  return (
    <PageShell title="Configurações" subtitle="Informações da conta e preferências do sistema.">

      <div className="space-y-4">
        {/* Dados da empresa — editáveis (owners/admins) */}
        <div className="card p-5">
          <h2 className="font-semibold text-ink mb-4 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-brand-700" />
            Empresa
          </h2>
          {loadingCompany ? (
            <div className="h-20 rounded-xl bg-surface-subtle animate-pulse" />
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-ink-muted mb-1">Nome da empresa</label>
                  <input className="input w-full" value={companyName} maxLength={120}
                    onChange={(e) => setCompanyName(e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs text-ink-muted mb-1">CNPJ / CPF</label>
                  <input className="input w-full" value={companyDoc} maxLength={18}
                    placeholder="00.000.000/0000-00"
                    onChange={(e) => setCompanyDoc(e.target.value)} />
                </div>
              </div>
              {companyMsg && (
                <p className={`text-xs ${companyMsg.ok ? 'text-positive' : 'text-negative'}`}>{companyMsg.text}</p>
              )}
              <button
                onClick={() => void handleSaveCompany()}
                disabled={savingCompany || !companyName.trim()}
                className="btn-secondary gap-2"
              >
                {savingCompany ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Salvar dados da empresa
              </button>
            </div>
          )}
        </div>

        {/* Dados do usuário */}
        <div className="card p-5">
          <h2 className="font-semibold text-ink mb-4 flex items-center gap-2">
            <User className="w-4 h-4 text-brand-700" />
            Conta
          </h2>
          <dl className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b border-border">
              <dt className="text-sm text-ink-muted flex items-center gap-2">
                <Mail className="w-4 h-4" />
                E-mail
              </dt>
              <dd className="text-sm font-medium text-ink">{user?.email ?? '—'}</dd>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-border">
              <dt className="text-sm text-ink-muted flex items-center gap-2">
                <Shield className="w-4 h-4" />
                ID do usuário
              </dt>
              <dd className="text-xs font-mono text-ink-soft truncate max-w-[220px]">{user?.id ?? '—'}</dd>
            </div>
            <div className="flex items-center justify-between py-2">
              <dt className="text-sm text-ink-muted">ID da empresa</dt>
              <dd className="text-xs font-mono text-ink-soft truncate max-w-[220px]">{tenantId ?? 'Não configurado'}</dd>
            </div>
          </dl>
        </div>

        {/* Segurança */}
        <div className="card p-5">
          <h2 className="font-semibold text-ink mb-4 flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-brand-700" />
            Segurança
          </h2>
          <p className="text-sm text-ink-muted mb-4">
            Enviaremos um link para o seu e-mail para que você possa definir uma nova senha.
          </p>

          {recoveryMsg && (
            <div className="mb-3 px-4 py-3 rounded-xl border border-positive/30 bg-positive/5 text-sm text-positive font-medium">
              ✓ {recoveryMsg}
            </div>
          )}
          {recoveryErr && (
            <div className="mb-3 px-4 py-3 rounded-xl border border-negative/30 bg-negative/5 text-sm text-negative">
              {recoveryErr}
            </div>
          )}

          <button
            onClick={() => void handleChangePassword()}
            disabled={sendingRecovery || recoveryCooldown > 0}
            className="btn-secondary gap-2"
          >
            {sendingRecovery && <Loader2 className="w-4 h-4 animate-spin" />}
            <KeyRound className="w-4 h-4" />
            {recoveryCooldown > 0 ? `Reenviar em ${recoveryCooldown}s` : 'Alterar senha'}
          </button>
        </div>

        {/* Sessão */}
        <div className="card p-5">
          <h2 className="font-semibold text-ink mb-4">Sessão</h2>
          <p className="text-sm text-ink-muted mb-4">
            Ao sair, você precisará fazer login novamente. Seus dados ficam salvos no banco de dados.
          </p>
          <button
            onClick={() => void handleSignOut()}
            className="btn bg-negative/10 hover:bg-negative/20 text-negative gap-2"
          >
            <LogOut className="w-4 h-4" />
            Sair da conta
          </button>
        </div>

        {/* Exclusão de conta */}
        <div className="card p-5 border-negative/30">
          <h2 className="font-semibold text-negative mb-2 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Excluir conta
          </h2>
          <p className="text-sm text-ink-muted mb-4">
            Exclui permanentemente sua conta e os dados da sua empresa (transações,
            contas, farms, pedidos e conversas). Seu login e senha são apagados e,
            para voltar, será necessário criar uma nova conta. Esta ação não pode ser
            desfeita.
          </p>
          {!deleteOpen ? (
            <button onClick={() => setDeleteOpen(true)} className="btn bg-negative hover:bg-negative/90 text-white gap-2">
              <Trash2 className="w-4 h-4" />
              Excluir minha conta
            </button>
          ) : !surveyStep ? (
            <div className="space-y-3">
              <label className="block text-xs text-ink-muted">
                Para confirmar, digite <span className="font-bold text-negative">EXCLUIR</span> abaixo:
              </label>
              <input className="input w-full max-w-xs" value={deleteText}
                onChange={(e) => setDeleteText(e.target.value)} placeholder="EXCLUIR" />
              {deleteErr && <p className="text-xs text-negative">{deleteErr}</p>}
              <div className="flex gap-2">
                <button
                  onClick={() => { setSurveyStep(true); setDeleteErr(''); }}
                  disabled={deleteText !== 'EXCLUIR'}
                  className="btn bg-negative hover:bg-negative/90 text-white gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Continuar
                </button>
                <button onClick={() => { setDeleteOpen(false); setDeleteText(''); }} className="btn-secondary">
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-ink">Antes de ir, conte para nós o motivo (nos ajuda a melhorar):</p>
              <div className="space-y-1.5">
                {[
                  'Não preciso mais do serviço',
                  'Faltam funcionalidades que eu preciso',
                  'Achei difícil de usar',
                  'Preço / custo',
                  'Encontrei outra solução',
                  'Preocupações com privacidade/segurança',
                  'Outro motivo',
                ].map((r) => (
                  <label key={r} className="flex items-center gap-2 text-sm text-ink cursor-pointer">
                    <input type="radio" name="leave-reason" value={r}
                      checked={surveyReason === r} onChange={() => setSurveyReason(r)} />
                    {r}
                  </label>
                ))}
              </div>
              <textarea className="input w-full" rows={2} maxLength={500}
                placeholder="Quer deixar um comentário? (opcional)"
                value={surveyComment} onChange={(e) => setSurveyComment(e.target.value)} />
              {deleteErr && <p className="text-xs text-negative">{deleteErr}</p>}
              <div className="flex gap-2">
                <button
                  onClick={() => void handleDeleteAccount()}
                  disabled={deleting}
                  className="btn bg-negative hover:bg-negative/90 text-white gap-2"
                >
                  {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {deleting ? 'Excluindo…' : 'Enviar e excluir definitivamente'}
                </button>
                <button onClick={() => { setSurveyStep(false); }} disabled={deleting} className="btn-secondary">
                  Voltar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </PageShell>
  );
}
